import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './Ozet.css';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Proje {
    projeID: number;
    projeAdi: string;
    ada: string | null;
    parsel: string | null;
    eklenmeTarihi: string;
    baslamaTarihi: string | null;
    bitisTarihi: string | null;
    projeLogo: string | null;
    takipNo: string;
    adres: string | null;
    sehir: string | null;
    kdv: number;
    projeDurumu: string;
    aktifMi: boolean;
}

interface Daire {
    daireID: number;
    daireNo: string;
    projeID: number;
    daireDurumu: string;
}

interface ProjeOzetDto {
    daireIstatistikleri: {
        toplam: number;
        satista: number;
        rezerve: number;
        sozlesme: number;
        arsaSahibi: number;
        taseron: number;
    };
    finansalIstatistikler: {
        sozlesmeSayisi: number;
        toplamSozlesmeFiyati: number;
        odenenToplam: number;
        bekleyenToplam: number;
        toplamOdeme: number;
    };
}

const Ozet: React.FC = () => {
    const { t } = useTranslation();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [selectedProjeId, setSelectedProjeId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [projeOzet, setProjeOzet] = useState<ProjeOzetDto | null>(null);
    const [ozetLoading, setOzetLoading] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchProjeler();
    }, []);

    useEffect(() => {
        if (selectedProjeId) {
            fetchProjeOzeti(selectedProjeId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjeId]);

    const fetchProjeler = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
            // İlk projeyi varsayılan olarak seç
            if (response.data.length > 0) {
                setSelectedProjeId(response.data[0].projeID);
            }
        } catch (error) {
            console.error('Projeler yüklenirken hata oluştu:', error);
            toast.error(t('msg.projelerYuklenirkenHata'));
        } finally {
            setLoading(false);
        }
    };

    // Tek API çağrısıyla proje özet istatistiklerini backend'den al
    // (Eski yöntem: 3 ayrı çağrı + frontend filter/reduce → artık kullanılmıyor)
    const fetchProjeOzeti = async (projeId: number) => {
        try {
            setOzetLoading(true);
            const response = await axiosInstance.get(`/api/Projeler/${projeId}/ozet`);
            setProjeOzet(response.data);
        } catch (error) {
            console.error('Proje özeti yüklenirken hata oluştu:', error);
            toast.error(t('msg.projeOzetiYuklenirkenHata'));
        } finally {
            setOzetLoading(false);
        }
    };

    const handleProjeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const projeId = parseInt(e.target.value);
        setSelectedProjeId(projeId);
    };

    const selectedProje = projeler.find(p => p.projeID === selectedProjeId);

    // Backend'den gelen özet verilerini hesap formatına dönüştür
    const stats = {
        satista:         projeOzet?.daireIstatistikleri.satista      ?? 0,
        rezerve:         projeOzet?.daireIstatistikleri.rezerve      ?? 0,
        sozlesme:        projeOzet?.daireIstatistikleri.sozlesme     ?? 0,
        arsaSahibi:      projeOzet?.daireIstatistikleri.arsaSahibi   ?? 0,
        taseron:         projeOzet?.daireIstatistikleri.taseron      ?? 0,
        toplam:          projeOzet?.daireIstatistikleri.toplam       ?? 0,
        satistaYuzde:    projeOzet ? ((projeOzet.daireIstatistikleri.satista    / (projeOzet.daireIstatistikleri.toplam || 1)) * 100).toFixed(1) : '0',
        rezerveYuzde:    projeOzet ? ((projeOzet.daireIstatistikleri.rezerve    / (projeOzet.daireIstatistikleri.toplam || 1)) * 100).toFixed(1) : '0',
        sozlesmeYuzde:   projeOzet ? ((projeOzet.daireIstatistikleri.sozlesme   / (projeOzet.daireIstatistikleri.toplam || 1)) * 100).toFixed(1) : '0',
        arsaSahibiYuzde: projeOzet ? ((projeOzet.daireIstatistikleri.arsaSahibi / (projeOzet.daireIstatistikleri.toplam || 1)) * 100).toFixed(1) : '0',
        taseronYuzde:    projeOzet ? ((projeOzet.daireIstatistikleri.taseron    / (projeOzet.daireIstatistikleri.toplam || 1)) * 100).toFixed(1) : '0',
    };

    const finansalStats = {
        sozlesmeToplamı: projeOzet?.finansalIstatistikler.toplamSozlesmeFiyati ?? 0,
        toplamOdeme:     projeOzet?.finansalIstatistikler.toplamOdeme           ?? 0,
        odenenToplam:    projeOzet?.finansalIstatistikler.odenenToplam          ?? 0,
        bekleyenToplam:  projeOzet?.finansalIstatistikler.bekleyenToplam        ?? 0,
        odenenYuzde:     projeOzet && projeOzet.finansalIstatistikler.toplamOdeme > 0
                             ? ((projeOzet.finansalIstatistikler.odenenToplam / projeOzet.finansalIstatistikler.toplamOdeme) * 100).toFixed(1)
                             : '0',
        bekleyenYuzde:   projeOzet && projeOzet.finansalIstatistikler.toplamOdeme > 0
                             ? ((projeOzet.finansalIstatistikler.bekleyenToplam / projeOzet.finansalIstatistikler.toplamOdeme) * 100).toFixed(1)
                             : '0',
    };

    // Daire Chart.js için veri hazırlama
    const chartData = {
        labels: [
            `${t('ozet.satista')} (${stats.satistaYuzde}%)`,
            `${t('durum.rezerve')} (${stats.rezerveYuzde}%)`,
            `${t('ozet.sozlesme')} (${stats.sozlesmeYuzde}%)`,
            `${t('ozet.arsaSahibi')} (${stats.arsaSahibiYuzde}%)`,
            `${t('ozet.taseron')} (${stats.taseronYuzde}%)`
        ],
        datasets: [
            {
                data: [stats.satista, stats.rezerve, stats.sozlesme, stats.arsaSahibi, stats.taseron],
                backgroundColor: [
                    '#cd6906',  // marka turuncu - Satışta
                    '#f1a45c',  // pastel turuncu - Rezerve
                    '#d4d4d8',  // gray-300 - Sözleşme
                    '#e4e4e7',  // gray-200 - Arsa Sahibi
                    '#f4f4f5',  // gray-100 - Taşeron
                ],
                borderColor: [
                    '#cd6906',
                    '#f1a45c',
                    '#d4d4d8',
                    '#e4e4e7',
                    '#f4f4f5',
                ],
                borderWidth: 0,
            },
        ],
    };

    const legendPosition: 'bottom' | 'right' = isMobile ? 'bottom' : 'right';

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: {
                position: legendPosition,
                labels: {
                    padding: 16,
                    boxWidth: 14,
                    boxHeight: 14,
                    borderRadius: 5,
                    useBorderRadius: true,
                    font: { size: 12, weight: 600 },
                    color: '#475569',
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context: any) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        return `${label}: ${value} ${t('ozet.daire')}`;
                    }
                }
            }
        }
    };

    // Ödeme durumu pasta grafiği için veri
    const odemeChartData = {
        labels: [
            `${t('ozet.odendi')} (${finansalStats.odenenYuzde}%)`,
            `${t('ozet.bekliyor')} (${finansalStats.bekleyenYuzde}%)`
        ],
        datasets: [
            {
                data: [finansalStats.odenenToplam, finansalStats.bekleyenToplam],
                backgroundColor: [
                    '#10b981',  // emerald-500 - Ödendi
                    '#cd6906',  // turuncu - Bekliyor
                ],
                borderColor: [
                    '#10b981',
                    '#cd6906',
                ],
                borderWidth: 0,
            },
        ],
    };

    const odemeChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: {
                position: legendPosition,
                labels: {
                    padding: 16,
                    boxWidth: 14,
                    boxHeight: 14,
                    borderRadius: 5,
                    useBorderRadius: true,
                    font: { size: 13, weight: 600 },
                    color: '#475569',
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context: any) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        return `${label}: ${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
                    }
                }
            }
        }
    };

    const formatCurrency = (amount: number): string => {
        return amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <>
            

            <div style={{ width: '100%', padding: '24px 24px', paddingBottom: '40px' }}>
                    {/* Sayfa Başlığı */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '40px' }}>
                        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>{t('ozet.baslik')}</h1>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>{t('ozet.altBaslik')}</p>
                    </div>

                    {/* Proje Seçimi */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div style={{
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f1f5f9',
                                boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                            }}>
                                {/* Sol: ikon + etiket + select */}
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: '#fff8f0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <i className="fas fa-layer-group" style={{ fontSize: '14px', color: '#cd6906' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: '#94a3b8', marginBottom: '4px' }}>
                                        {t('ozet.projeSeciniz')}
                                    </div>
                                    {loading ? (
                                        <div className="d-flex align-items-center" style={{ color: '#64748b', fontSize: '13px', gap: '8px' }}>
                                            <Spinner animation="border" size="sm" />
                                            {t('msg.projelerYukleniyor')}
                                        </div>
                                    ) : projeler.length === 0 ? (
                                        <div style={{ fontSize: '13px', color: '#f59e0b' }}>
                                            <i className="fas fa-exclamation-triangle me-2" />
                                            {t('msg.henuzProjeEklenmemis')}
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative', maxWidth: '420px' }}>
                                            <select
                                                value={selectedProjeId || ''}
                                                onChange={handleProjeChange as any}
                                                style={{
                                                    width: '100%',
                                                    minWidth: '300px',
                                                    appearance: 'none',
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    padding: '7px 36px 7px 12px',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    outline: 'none',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                                                    transition: 'border-color 0.15s, box-shadow 0.15s',
                                                }}
                                                onFocus={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.12)'; }}
                                                onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; }}
                                            >
                                                {projeler.map((proje) => (
                                                    <option key={proje.projeID} value={proje.projeID}>
                                                        {proje.projeAdi} — {proje.projeDurumu}
                                                    </option>
                                                ))}
                                            </select>
                                            {/* Chevron icon */}
                                            <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }}
                                                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                {/* Active badge */}
                                {selectedProjeId && !loading && (
                                    <span style={{
                                        fontSize: '11px', fontWeight: 600,
                                        padding: '3px 10px', borderRadius: '9999px',
                                        background: '#dcfce7', color: '#16a34a',
                                        flexShrink: 0,
                                    }}>{t('durum.aktif')}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* İstatistikler - Proje Seçildiğinde Gösterilecek */}
                    {selectedProje && (
                        <>
                            {ozetLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">{t('msg.projeOzetiYukleniyor')}</span>
                                    </Spinner>
                                </div>
                            ) : (
                                <>
                                    {/* ── İstatistik Kartları ── */}
                                    <div className="ozet-stat-grid">
                                        {[
                                            {
                                                label: t('ozet.toplamDaire'),
                                                value: stats.toplam,
                                                pct: null,
                                                icon: 'fas fa-building',
                                                iconBg: '#fff8f0', iconColor: '#cd6906',
                                            },
                                            {
                                                label: t('ozet.satista'),
                                                value: stats.satista,
                                                pct: stats.satistaYuzde,
                                                icon: 'fas fa-store',
                                                iconBg: '#fff8f0', iconColor: '#cd6906',
                                            },
                                            {
                                                label: t('durum.rezerve'),
                                                value: stats.rezerve,
                                                pct: stats.rezerveYuzde,
                                                icon: 'fas fa-clock',
                                                iconBg: '#f4f4f5', iconColor: '#3f3f46',
                                            },
                                            {
                                                label: t('ozet.sozlesme'),
                                                value: stats.sozlesme,
                                                pct: stats.sozlesmeYuzde,
                                                icon: 'fas fa-file-contract',
                                                iconBg: '#f4f4f5', iconColor: '#3f3f46',
                                            },
                                            {
                                                label: t('ozet.arsaSahibi'),
                                                value: stats.arsaSahibi,
                                                pct: stats.arsaSahibiYuzde,
                                                icon: 'fas fa-user-tie',
                                                iconBg: '#f4f4f5', iconColor: '#3f3f46',
                                            },
                                            {
                                                label: t('ozet.taseron'),
                                                value: stats.taseron,
                                                pct: stats.taseronYuzde,
                                                icon: 'fas fa-hard-hat',
                                                iconBg: '#f4f4f5', iconColor: '#3f3f46',
                                            },
                                        ].map((item) => (
                                            <div
                                                key={item.label}
                                                style={{
                                                    background: '#fff',
                                                    borderRadius: '12px',
                                                    border: '1px solid #f1f5f9',
                                                    boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
                                                    padding: '16px 18px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '12px',
                                                    transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
                                                    cursor: 'default',
                                                }}
                                                onMouseEnter={e => {
                                                    const el = e.currentTarget as HTMLDivElement;
                                                    el.style.borderColor = '#cd6906';
                                                    el.style.boxShadow = '0 4px 12px rgba(205,105,6,0.15), 0 1px 3px rgba(15,23,42,0.08)';
                                                    el.style.transform = 'translateY(-3px)';
                                                }}
                                                onMouseLeave={e => {
                                                    const el = e.currentTarget as HTMLDivElement;
                                                    el.style.borderColor = '#f1f5f9';
                                                    el.style.boxShadow = '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)';
                                                    el.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                {/* İkon sol — Rakam+Etiket sağ */}
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                                    {/* İkon */}
                                                    <div style={{
                                                        width: '54px', height: '54px', borderRadius: '14px',
                                                        background: item.iconBg,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                        <i className={item.icon} style={{ fontSize: '22px', color: item.iconColor }} />
                                                    </div>
                                                    {/* Rakam + Etiket */}
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', justifyContent: 'flex-end' }}>
                                                            <span style={{
                                                                fontSize: '30px', fontWeight: 700,
                                                                color: '#0f172a', letterSpacing: '-0.04em', lineHeight: 1,
                                                            }}>
                                                                {item.value}
                                                            </span>
                                                            {item.pct !== null && (
                                                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8', letterSpacing: '-0.01em' }}>
                                                                    %{item.pct}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '10px', fontWeight: 600,
                                                            color: '#64748b', marginTop: '5px',
                                                            letterSpacing: '0.5px',
                                                        }}>
                                                            {item.label}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <Row>
                                        <Col lg={6} className="mb-4">
                                            <Card style={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                                                <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-chart-pie" style={{ fontSize: '12px', color: '#cd6906' }} />
                                                        </div>
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{t('ozet.daireDurumuDagilimi')}</span>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body style={{ padding: '20px' }}>
                                                    {stats.toplam > 0 ? (
                                                        <div style={{ height: '320px', position: 'relative' }}>
                                                            <Doughnut data={chartData} options={chartOptions} />
                                                        </div>
                                                    ) : (
                                                        <div className="alert alert-info">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            {t('msg.projeDaireYok')}
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col lg={6} className="mb-4">
                                            <Card style={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                                                <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-info-circle" style={{ fontSize: '12px', color: '#16a34a' }} />
                                                        </div>
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{t('ozet.projeBilgileri')}</span>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            {t('ozet.projeAdi')}
                                                            <span>{selectedProje.projeAdi}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            {t('ozet.projeDurumu')}
                                                            <span style={{
                                                                background: selectedProje.projeDurumu === 'Tamamlandı' ? '#f0fdf4' :
                                                                            selectedProje.projeDurumu === 'Devam Ediyor' ? '#fff8f0' : '#f0f9ff',
                                                                color: selectedProje.projeDurumu === 'Tamamlandı' ? '#166534' :
                                                                       selectedProje.projeDurumu === 'Devam Ediyor' ? '#92400e' : '#0369a1',
                                                                padding: '4px 12px',
                                                                borderRadius: '100px',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                display: 'inline-block',
                                                                border: `1px solid ${selectedProje.projeDurumu === 'Tamamlandı' ? '#bbf7d0' : selectedProje.projeDurumu === 'Devam Ediyor' ? '#fed7aa' : '#bae6fd'}`,
                                                            }}>
                                                                {selectedProje.projeDurumu}
                                                            </span>
                                                        </div>
                                                        {selectedProje.sehir && (
                                                            <div className="d-flex justify-content-between mb-2">
                                                                {t('ozet.sehir')}
                                                                <span>{selectedProje.sehir}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{ marginTop: '16px' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '12px' }}>{t('ozet.daireDagilimi')}</div>
                                                        {[
                                            { label: t('ozet.satista'),   value: stats.satista,    pct: stats.satistaYuzde,    color: '#cd6906', track: '#f4f4f5' },
                                                        { label: t('durum.rezerve'), value: stats.rezerve,    pct: stats.rezerveYuzde,    color: '#f1a45c', track: '#f4f4f5' },
                                                        { label: t('ozet.sozlesme'), value: stats.sozlesme,   pct: stats.sozlesmeYuzde,   color: '#d4d4d8', track: '#f4f4f5' },
                                                        { label: t('ozet.arsaSahibi'), value: stats.arsaSahibi, pct: stats.arsaSahibiYuzde, color: '#e4e4e7', track: '#f4f4f5' },
                                                        { label: t('ozet.taseron'), value: stats.taseron,    pct: stats.taseronYuzde,    color: '#f4f4f5', track: '#f4f4f5' },
                                                        ].map(item => (
                                                            <div key={item.label} style={{ marginBottom: '10px' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block', flexShrink: 0 }} />
                                                                        {item.label}
                                                                    </span>
                                                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                                                                        {item.value} <span style={{ fontWeight: 400, color: '#94a3b8' }}>%{item.pct}</span>
                                                                    </span>
                                                                </div>
                                                                <div
                                                                    title={`${item.label}: ${item.value} daire (%${item.pct})`}
                                                                    style={{ height: '6px', borderRadius: '9999px', background: item.track, overflow: 'hidden' }}
                                                                >
                                                                    <div style={{ height: '100%', width: `${item.pct}%`, background: item.color, borderRadius: '9999px', transition: 'width 0.4s ease' }} />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Finansal Bilgiler */}
                                    <Row>
                                        <Col lg={12} className="mb-4">
                                            <Card style={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                                                <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-file-invoice-dollar" style={{ fontSize: '12px', color: '#cd6906' }} />
                                                        </div>
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{t('ozet.sozlesmeBilgileri')}</span>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body>
                                                    {ozetLoading ? (
                                                        <div className="d-flex justify-content-center py-3">
                                                            <Spinner animation="border" size="sm" />
                                                        </div>
                                                    ) : (
                                                        <Row className="g-3">
                                                            <Col md={4}>
                                                                <div style={{ padding: '20px 24px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e9edf2', textAlign: 'center' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase' }}>{t('ozet.toplamSozlesme')}</div>
                                                                    <div style={{ fontSize: '36px', fontWeight: 800, color: '#151515', lineHeight: 1 }}>{projeOzet?.finansalIstatistikler.sozlesmeSayisi ?? 0}</div>
                                                                    <div style={{ fontSize: '11px', color: '#b0b8c4', marginTop: '6px' }}>{t('label.adet')}</div>
                                                                </div>
                                                            </Col>
                                                            <Col md={8}>
                                                                <div style={{ padding: '20px 24px', borderRadius: '12px', background: '#fff8f0', border: '1px solid #fde8cc', textAlign: 'center' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase' }}>{t('ozet.sozlesmeToplamı')}</div>
                                                                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#cd6906', lineHeight: 1, letterSpacing: '-0.03em' }}>{formatCurrency(finansalStats.sozlesmeToplamı)}</div>
                                                                    <div style={{ fontSize: '11px', color: '#b0b8c4', marginTop: '6px' }}>{t('label.turkLirasi')}</div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col lg={6} className="mb-4">
                                            <Card style={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                                                <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-money-bill-wave" style={{ fontSize: '12px', color: '#16a34a' }} />
                                                        </div>
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{t('ozet.odemeBilgileri')}</span>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body style={{ padding: '20px' }}>
                                                    {/* Özet satırları */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                                        {[
                                                            { label: t('ozet.toplamGelir'), value: finansalStats.toplamOdeme,   valueColor: '#0f172a', iconBg: '#fff8f0', iconColor: '#cd6906', icon: 'fas fa-chart-line' },
                                                            { label: t('ozet.odendi'), value: finansalStats.odenenToplam, valueColor: '#16a34a', iconBg: '#f0fdf4', iconColor: '#16a34a', icon: 'fas fa-check-circle' },
                                                            { label: t('ozet.bekliyor'), value: finansalStats.bekleyenToplam, valueColor: '#cd6906', iconBg: '#fffbeb', iconColor: '#d97706', icon: 'fas fa-clock' },
                                                        ].map(r => (
                                                            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: r.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <i className={r.icon} style={{ fontSize: '11px', color: r.iconColor }} />
                                                                    </div>
                                                                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>{r.label}</span>
                                                                </div>
                                                                <span style={{ fontSize: '14px', fontWeight: 700, color: r.valueColor }}>{formatCurrency(r.value)} ₺</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* Progress bars */}
                                                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '12px' }}>{t('ozet.odemeDurumuDagilimi')}</div>
                                                    {[
                                                        { label: t('ozet.odendi'), value: finansalStats.odenenToplam, pct: finansalStats.odenenYuzde,   color: '#10b981', track: '#f4f4f5' },
                                                        { label: t('ozet.bekliyor'), value: finansalStats.bekleyenToplam, pct: finansalStats.bekleyenYuzde, color: '#cd6906', track: '#f4f4f5' },
                                                    ].map(item => (
                                                        <div key={item.label} style={{ marginBottom: '10px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block', flexShrink: 0 }} />
                                                                    {item.label}
                                                                </span>
                                                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                                                                    {formatCurrency(item.value)} ₺ <span style={{ fontWeight: 400, color: '#94a3b8' }}>%{item.pct}</span>
                                                                </span>
                                                            </div>
                                                            <div
                                                                title={`${item.label}: ${formatCurrency(item.value)} ₺ (%${item.pct})`}
                                                                style={{ height: '6px', borderRadius: '9999px', background: item.track, overflow: 'hidden' }}
                                                            >
                                                                <div style={{ height: '100%', width: `${item.pct}%`, background: item.color, borderRadius: '9999px', transition: 'width 0.4s ease' }} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        <Col lg={6} className="mb-4">
                                            <Card style={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                                                <Card.Header style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="fas fa-chart-pie" style={{ fontSize: '12px', color: '#d97706' }} />
                                                        </div>
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{t('ozet.odemeDurumuGrafigi')}</span>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body style={{ padding: '20px' }}>
                                                    {finansalStats.toplamOdeme > 0 ? (
                                                        <div style={{ height: '320px', position: 'relative' }}>
                                                            <Doughnut data={odemeChartData} options={odemeChartOptions} />
                                                        </div>
                                                    ) : (
                                                        <div className="alert alert-info">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            {t('msg.projeOdemeYok')}
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </>
                    )}
            </div>
        </>
    );
};

export default Ozet;

